/**
 * A deliberately small Markdown renderer.
 *
 * ## Why not `marked` or `react-markdown`
 *
 * Because the output of both is either an HTML string - which means
 * `dangerouslySetInnerHTML`, which means the sanitiser becomes the security
 * boundary and the sanitiser is the part nobody tests - or a dependency tree of
 * twenty packages to render a card description.
 *
 * This renderer never produces HTML. It produces React elements, so the only way
 * for user input to reach the DOM is as a text node or as an attribute value
 * that React escapes. There is no `dangerouslySetInnerHTML` anywhere in this
 * application, which is a property that can be verified with one search rather
 * than by reasoning about a parser's edge cases.
 *
 * The subset is chosen to be the subset people actually use on a task card:
 * paragraphs, bullet and numbered lists, three heading levels, fenced code,
 * bold, italic, strikethrough, inline code, links, and `@mentions`. Anything
 * else renders as literal text, which is the honest outcome - it is what the
 * author typed.
 *
 * ## URL handling
 *
 * A link target is checked against an allow-list of schemes (`http`, `https`,
 * `mailto`) before it is rendered. A `javascript:` URL in an `href` is a real
 * XSS vector and React does not block it - React warns in development and emits
 * it in production. `data:` is excluded for the same reason. A rejected link
 * renders as its own label with no anchor, so the text is still readable.
 *
 * Relative targets are permitted and normalised by the browser. Protocol-relative
 * targets (`//evil.example`) are rejected: they inherit the page's scheme and are
 * a common way to smuggle an off-site link past a naive `startsWith('http')`
 * check.
 */

import type { ReactNode } from 'react';

const SAFE_SCHEMES = new Set(['http:', 'https:', 'mailto:']);

function isSafeHref(href: string): boolean {
  const trimmed = href.trim();
  if (trimmed === '') return false;
  if (trimmed.startsWith('//')) return false;

  // No scheme at all means a relative link, which is safe by construction.
  if (!/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed)) return true;

  try {
    // Resolved against a fixed base so that a scheme-only value like `mailto:`
    // is classified rather than throwing.
    return SAFE_SCHEMES.has(new URL(trimmed, 'https://slate.invalid/').protocol);
  } catch {
    return false;
  }
}

/**
 * Inline spans.
 *
 * Ordered alternation, applied in a single pass rather than in successive
 * `String.replace` calls. Successive replacements are the classic Markdown bug:
 * `**bold**` becomes `<strong>bold</strong>` and then the `_` inside the
 * replacement is matched by the emphasis pass, so a description containing
 * `snake_case_word` renders differently depending on whether it is bolded.
 */
const INLINE_PATTERN = new RegExp(
  [
    '(`[^`\\n]+`)', // 1 inline code
    '(\\*\\*[^*\\n]+\\*\\*)', // 2 bold
    '(__[^_\\n]+__)', // 3 bold
    '(~~[^~\\n]+~~)', // 4 strikethrough
    '(\\*[^*\\n]+\\*)', // 5 italic
    '(@[A-Za-z0-9._-]+)', // 6 mention
    '(!?\\[[^\\]\\n]*\\]\\([^)\\s]+\\))', // 7 link
  ].join('|'),
  'g',
);

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let counter = 0;

  // `matchAll` rather than a `while (exec)` loop: the regex is global and shared
  // across calls, and `lastIndex` on a module-level global regex is state that
  // leaks between renders.
  for (const match of text.matchAll(INLINE_PATTERN)) {
    const index = match.index ?? 0;
    if (index > lastIndex) nodes.push(text.slice(lastIndex, index));
    lastIndex = index + match[0].length;
    const key = `${keyPrefix}-i${counter}`;
    counter += 1;

    const [raw, code, boldStar, boldUnderscore, strike, italic, mention, link] = match;

    if (code !== undefined) {
      nodes.push(
        <code key={key} className="rounded bg-sunken px-1 py-0.5 font-mono text-[0.85em] text-ink">
          {code.slice(1, -1)}
        </code>,
      );
      continue;
    }
    if (boldStar !== undefined || boldUnderscore !== undefined) {
      const body = boldStar !== undefined ? boldStar.slice(2, -2) : (boldUnderscore ?? '').slice(2, -2);
      nodes.push(
        <strong key={key} className="font-semibold text-ink">
          {body}
        </strong>,
      );
      continue;
    }
    if (strike !== undefined) {
      nodes.push(
        <s key={key} className="text-ink-subtle">
          {strike.slice(2, -2)}
        </s>,
      );
      continue;
    }
    if (italic !== undefined) {
      nodes.push(<em key={key}>{italic.slice(1, -1)}</em>);
      continue;
    }
    if (mention !== undefined) {
      nodes.push(
        <span key={key} className="rounded bg-accent-soft px-1 font-medium text-accent">
          {mention}
        </span>,
      );
      continue;
    }
    if (link !== undefined) {
      // `![alt](src)` renders as a link, not as an `<img>`. A remote image in a
      // description is a tracking pixel, it leaks the board URL in `Referer`,
      // and it makes the reader's browser fetch from an origin the author
      // chose. Rendering the label as a link keeps the target reachable without
      // any of that.
      const isImage = link.startsWith('!');
      const body = isImage ? link.slice(1) : link;
      const separator = body.indexOf('](');
      const label = body.slice(1, separator);
      const href = body.slice(separator + 2, -1);

      if (!isSafeHref(href)) {
        // Rendered as text, not as an anchor. Losing the link is the correct
        // outcome for a target the renderer cannot vouch for.
        nodes.push(<span key={key}>{label || href}</span>);
        continue;
      }

      nodes.push(
        <a
          key={key}
          href={href}
          // Every link out of a user-authored description opens in a new
          // context. `noreferrer` is not decoration: without it the target
          // origin receives the board URL in `Referer`, and a board URL is a
          // capability to a private workspace.
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="text-accent underline decoration-accent/40 underline-offset-2 hover:decoration-accent"
        >
          {label || href}
        </a>,
      );
      continue;
    }

    // Unreachable: every alternative is handled above. Kept so an added
    // alternative fails visibly rather than dropping text.
    nodes.push(raw);
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

/** Block-level parsing. Line-oriented, which is what makes it small enough to
 * read in one sitting and therefore to trust. */
export function renderMarkdown(source: string, keyPrefix = 'md'): ReactNode {
  const lines = source.replace(/\r\n?/g, '\n').split('\n');
  const blocks: ReactNode[] = [];

  let paragraph: string[] = [];
  let listItems: { ordered: boolean; text: string }[] = [];
  let fence: string[] | null = null;
  let counter = 0;

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    const key = `${keyPrefix}-p${counter}`;
    counter += 1;
    blocks.push(
      <p key={key} className="my-2 leading-relaxed first:mt-0 last:mb-0">
        {renderInline(paragraph.join(' '), key)}
      </p>,
    );
    paragraph = [];
  };

  const flushList = () => {
    if (listItems.length === 0) return;
    const key = `${keyPrefix}-l${counter}`;
    counter += 1;
    const ordered = listItems[0]?.ordered === true;

    const children = listItems.map((item, position) => (
      <li key={`${key}-${position}`} className="leading-relaxed">
        {renderInline(item.text, `${key}-${position}`)}
      </li>
    ));

    blocks.push(
      ordered ? (
        <ol key={key} className="my-2 list-decimal space-y-1 pl-5">
          {children}
        </ol>
      ) : (
        <ul key={key} className="my-2 list-disc space-y-1 pl-5">
          {children}
        </ul>
      ),
    );
    listItems = [];
  };

  const flushAll = () => {
    flushParagraph();
    flushList();
  };

  for (const line of lines) {
    // Inside a fence everything is literal until the closing fence.
    if (fence !== null) {
      if (line.trimStart().startsWith('```')) {
        const key = `${keyPrefix}-c${counter}`;
        counter += 1;
        blocks.push(
          <pre
            key={key}
            className="scroll-slim my-2 overflow-x-auto rounded-md border border-border bg-sunken p-3 font-mono text-xs leading-relaxed text-ink"
          >
            <code>{fence.join('\n')}</code>
          </pre>,
        );
        fence = null;
      } else {
        fence.push(line);
      }
      continue;
    }

    if (line.trimStart().startsWith('```')) {
      flushAll();
      fence = [];
      continue;
    }

    if (line.trim() === '') {
      flushAll();
      continue;
    }

    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    if (heading !== null) {
      flushAll();
      const level = (heading[1] ?? '#').length;
      const key = `${keyPrefix}-h${counter}`;
      counter += 1;
      const body = renderInline(heading[2] ?? '', key);
      // The board already has an h1 (the board name) and an h2 per column, so
      // descriptions start at h3. Skipping levels is an accessibility failure,
      // and a card description is not a document outline.
      if (level === 1) {
        blocks.push(
          <h3 key={key} className="mt-3 mb-1 text-base font-semibold text-ink first:mt-0">
            {body}
          </h3>,
        );
      } else if (level === 2) {
        blocks.push(
          <h4 key={key} className="mt-3 mb-1 text-sm font-semibold text-ink first:mt-0">
            {body}
          </h4>,
        );
      } else {
        blocks.push(
          <h5 key={key} className="mt-2 mb-1 text-xs font-semibold tracking-wide text-ink-muted uppercase first:mt-0">
            {body}
          </h5>,
        );
      }
      continue;
    }

    const bullet = /^\s*[-*+]\s+(.*)$/.exec(line);
    if (bullet !== null) {
      flushParagraph();
      if (listItems.some((item) => item.ordered)) flushList();
      listItems.push({ ordered: false, text: bullet[1] ?? '' });
      continue;
    }

    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (numbered !== null) {
      flushParagraph();
      if (listItems.some((item) => !item.ordered)) flushList();
      listItems.push({ ordered: true, text: numbered[1] ?? '' });
      continue;
    }

    // Any line that is not a list item ends the list. Lazy continuation - a
    // bare line belonging to the preceding list item - is the one piece of
    // CommonMark this renderer does not implement, and without this flush the
    // paragraph would be emitted *before* the list it follows, because
    // `flushAll` closes the paragraph before it closes the list.
    if (listItems.length > 0) flushList();

    // A continuation of the current paragraph. Soft-wrapped prose is common in
    // a textarea and joining with a space is what the author meant.
    paragraph.push(line.trim());
  }

  // An unterminated fence is not an error. A description being typed live will
  // routinely be mid-fence, and rendering the partial block is better than
  // rendering nothing.
  if (fence !== null) {
    const key = `${keyPrefix}-c${counter}`;
    blocks.push(
      <pre
        key={key}
        className="scroll-slim my-2 overflow-x-auto rounded-md border border-border bg-sunken p-3 font-mono text-xs leading-relaxed text-ink"
      >
        <code>{fence.join('\n')}</code>
      </pre>,
    );
  }
  flushAll();

  return <>{blocks}</>;
}

/** Plain-text extraction for the card tile preview. Strips the syntax characters
 * rather than rendering a truncated tree, because a two-line preview of a card
 * description wants the words, not the structure. */
export function toPlainText(source: string, limit = 180): string {
  const stripped = source
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/\*\*([^*]*)\*\*/g, '$1')
    .replace(/__([^_]*)__/g, '$1')
    .replace(/~~([^~]*)~~/g, '$1')
    .replace(/(^|\s)[*_](\S[^*_]*)[*_]/g, '$1$2')
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+[.)]\s+/gm, '')
    .replace(/\s+/g, ' ')
    .trim();

  return stripped.length > limit ? `${stripped.slice(0, limit - 1).trimEnd()}…` : stripped;
}
